/**
 * Module-level store for the boards of the most recent speaking session.
 *
 * `useWhiteboard` lives inside `TranscriptionView`, which the main app
 * unmounts the moment a session ends — taking the in-hook board history with
 * it. This store keeps the last session's boards alive across that unmount so
 * `ListenView` can show the end-of-session board recap. Cleared when the
 * recap is dismissed or a new session records its first board.
 */
import type { WhiteboardSessionBoard } from "./boardResults";

let lastSessionBoards: WhiteboardSessionBoard[] = [];
const listeners = new Set<() => void>();

function notifyLastSessionBoardsChanged(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function setLastSessionBoards(boards: WhiteboardSessionBoard[]): void {
  lastSessionBoards = boards;
  notifyLastSessionBoardsChanged();
}

export function getLastSessionBoards(): WhiteboardSessionBoard[] {
  return lastSessionBoards;
}

export function clearLastSessionBoards(): void {
  lastSessionBoards = [];
  notifyLastSessionBoardsChanged();
}

export function subscribeLastSessionBoards(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Boards worth recapping: the learner actually engaged with them. */
export function getEngagedLastSessionBoards(): WhiteboardSessionBoard[] {
  return lastSessionBoards.filter(
    (board) =>
      board.result.label &&
      (board.result.status === "resolved" ||
        board.result.status === "attempted" ||
        board.result.attempts > 0)
  );
}
